import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import NetworkSectionSelector from './NetworkSectionSelector';

const AccessRequestReviewModal = ({ open, onClose, request, onRequestProcessed }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Editable fields for approval
  const [role, setRole] = useState('viewer');
  const [permissionScope, setPermissionScope] = useState('network_section');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [assignableResources, setAssignableResources] = useState({ networks: [] });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && request) {
      // Initialize from request data
      setRole(request.role || 'viewer');
      setPermissionScope('network_section');
      setSelectedPermissions(request.requested_locations || []);
      setError(null);
      setShowRejectForm(false);
      setRejectReason('');
      setNotes('');
      fetchResources();
    }
  }, [open, request]);

  const fetchResources = async () => {
    try {
      const response = await authService.getAvailableResources();
      setAssignableResources(response || { networks: [] });
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      await authService.approveAccessRequest(request.id, {
        locations: selectedPermissions,
        role,
        permissionScope,
        notes: notes || undefined
      });
      onRequestProcessed?.();
      onClose();
    } catch (err) {
      setError(err.error || 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authService.rejectAccessRequest(request.id, rejectReason);
      onRequestProcessed?.();
      onClose();
    } catch (err) {
      setError(err.error || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  const requestedDisciplines = request.requested_disciplines || [];
  const requestedSubDisciplines = request.requested_sub_disciplines || [];
  const requestedLocations = request.requested_locations || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Review Access Request</Typography>
          </Box>
          <Button onClick={onClose} size="small" color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* User Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>User Information</Typography>
          <Stack spacing={0.5}>
            <Typography><b>Name:</b> {request.full_name || request.username}</Typography>
            <Typography><b>Email:</b> {request.email}</Typography>
            <Typography><b>Username:</b> {request.username}</Typography>
            <Typography><b>Requested:</b> {new Date(request.created_at).toLocaleString()}</Typography>
          </Stack>
        </Paper>

        {/* Requested Disciplines (read-only, already applied) */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CategoryIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Disciplines (Already Applied)</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {requestedDisciplines.map((d, i) => (
              <Chip key={i} label={d.name} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
          {requestedSubDisciplines.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Sub-disciplines:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {requestedSubDisciplines.map((sd, i) => (
                  <Chip key={i} label={sd} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Requested Locations (read-only display) */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocationIcon fontSize="small" color="warning" />
            <Typography variant="subtitle2">Requested Locations (User's Preference)</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {requestedLocations.map((loc, i) => (
              <Chip
                key={i}
                label={`${loc.type === 'network' ? 'Network' : 'Section'}: ${loc.name}`}
                size="small"
                color={loc.type === 'network' ? 'info' : 'default'}
                variant="outlined"
              />
            ))}
            {requestedLocations.length === 0 && (
              <Typography variant="body2" color="text.secondary">No locations requested</Typography>
            )}
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Manager Decision
        </Typography>

        {/* Role & Scope Selection */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select value={role} onChange={(e) => setRole(e.target.value)} label="Role">
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="inspector">Inspector</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Permission Scope</InputLabel>
            <Select
              value={permissionScope}
              onChange={(e) => setPermissionScope(e.target.value)}
              label="Permission Scope"
            >
              <MenuItem value="organization">Organization-wide</MenuItem>
              <MenuItem value="network_section">Network/Section</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Location Assignment (editable) */}
        {permissionScope === 'network_section' && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assign Locations (you can modify the user's request)
            </Typography>
            <NetworkSectionSelector
              assignableResources={assignableResources}
              selectedPermissions={selectedPermissions}
              onChange={setSelectedPermissions}
              error={null}
            />
          </Paper>
        )}

        {permissionScope === 'organization' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Organization-wide access grants access to all networks and sections. No specific location assignment needed.
          </Alert>
        )}

        {/* Notes */}
        <TextField
          fullWidth
          size="small"
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        {/* Reject Form */}
        {showRejectForm && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
            <Typography variant="subtitle2" color="error" gutterBottom>Rejection Reason (required)</Typography>
            <TextField
              fullWidth
              size="small"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              multiline
              rows={2}
              placeholder="Explain why the request is being rejected..."
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleReject}
                disabled={loading || !rejectReason.trim()}
                startIcon={loading ? <CircularProgress size={14} /> : <RejectIcon />}
              >
                Confirm Reject
              </Button>
              <Button size="small" onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>
                Cancel
              </Button>
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!showRejectForm && (
          <Button
            color="error"
            onClick={() => setShowRejectForm(true)}
            disabled={loading}
          >
            Reject
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleApprove}
          disabled={loading || (permissionScope === 'network_section' && selectedPermissions.length === 0)}
          startIcon={loading ? <CircularProgress size={16} /> : <ApproveIcon />}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessRequestReviewModal;
